import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  CreateAutomationDocument,
  type CreateAutomationInput,
  CreateAutomationVersionDocument,
  DeleteAutomationDocument,
  DeleteAutomationVersionDocument,
  GoLiveAutomationVersionDocument,
  RenameAutomationVersionDocument,
  SaveAutomationGraphDocument,
  type SaveAutomationGraphInput,
  UpdateAutomationDocument,
  type UpdateAutomationInput,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";

// All automation mutations in one hook (the app's "consolidated mutations"
// convention). List-affecting mutations refetch GetAutomations; graph/version
// mutations update the normalized Automation/AutomationVersion entities by id.
export function useAutomationMutations() {
  const [createMutation] = useMutation(CreateAutomationDocument);
  const [updateMutation] = useMutation(UpdateAutomationDocument);
  const [saveGraphMutation] = useMutation(SaveAutomationGraphDocument);
  const [deleteMutation] = useMutation(DeleteAutomationDocument);
  const [goLiveMutation] = useMutation(GoLiveAutomationVersionDocument);
  const [createVersionMutation] = useMutation(CreateAutomationVersionDocument);
  const [renameVersionMutation] = useMutation(RenameAutomationVersionDocument);
  const [deleteVersionMutation] = useMutation(DeleteAutomationVersionDocument);

  const createAutomation = useCallback(
    async (input: CreateAutomationInput) => {
      try {
        const { data } = await createMutation({
          variables: { input },
          refetchQueries: ["GetAutomations"],
        });
        const result = data?.createAutomation;
        if (result?.__typename === "Automation") return result;
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to create automation", type: "error" });
        }
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create automation");
        return null;
      }
    },
    [createMutation],
  );

  const updateAutomation = useCallback(
    async (id: string, input: UpdateAutomationInput) => {
      try {
        const { data } = await updateMutation({ variables: { id, input } });
        const result = data?.updateAutomation;
        if (result?.__typename === "Automation") return result;
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to update automation", type: "error" });
        }
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update automation");
        return null;
      }
    },
    [updateMutation],
  );

  // Saves the whole graph of ONE (unpublished) version. Returns the validation
  // error (if any) so the editor can surface it inline rather than only
  // toasting.
  const saveGraph = useCallback(
    async (input: SaveAutomationGraphInput) => {
      try {
        const { data } = await saveGraphMutation({ variables: { input } });
        const result = data?.saveAutomationGraph;
        if (result?.__typename === "Automation") {
          return { ok: true as const, automation: result };
        }
        if (result?.__typename === "ValidationError") {
          return {
            ok: false as const,
            error: result.message ?? "Invalid automation",
            field: result.field ?? null,
          };
        }
        return { ok: false as const, error: "Failed to save automation", field: null };
      } catch (err) {
        toastMutationError(err, "Failed to save automation");
        return { ok: false as const, error: "Failed to save automation", field: null };
      }
    },
    [saveGraphMutation],
  );

  // Make a version THE live one ("Go live"): first go-live freezes it
  // (publishedAt), the pointer repoints, the automation turns on. Going live
  // with a PAST version is the revert path — same call. Returns ok/error so
  // the editor can surface a validation error inline.
  const goLiveVersion = useCallback(
    async (automationId: string, versionId: string, notes?: string) => {
      try {
        const { data } = await goLiveMutation({
          variables: { automationId, versionId, notes: notes ?? null },
        });
        const result = data?.goLiveAutomationVersion;
        if (result?.__typename === "AutomationVersionPayload") {
          return {
            ok: true as const,
            automation: result.automation,
            version: result.version,
          };
        }
        if (result?.__typename === "ValidationError") {
          return {
            ok: false as const,
            error: result.message ?? "Failed to go live",
            field: result.field ?? null,
          };
        }
        return { ok: false as const, error: "Failed to go live", field: null };
      } catch (err) {
        toastMutationError(err, "Failed to go live");
        return { ok: false as const, error: "Failed to go live", field: null };
      }
    },
    [goLiveMutation],
  );

  // Create a new EDITING version cloned from a source version (default: the
  // live one, else the newest). Returns the new version so the editor can
  // select it.
  const createVersion = useCallback(
    async (automationId: string, opts?: { fromVersionId?: string; name?: string }) => {
      try {
        const { data } = await createVersionMutation({
          variables: {
            automationId,
            fromVersionId: opts?.fromVersionId ?? null,
            name: opts?.name ?? null,
          },
        });
        const result = data?.createAutomationVersion;
        if (result?.__typename === "AutomationVersionPayload") {
          return {
            ok: true as const,
            automation: result.automation,
            version: result.version,
          };
        }
        if (result?.__typename === "ValidationError") {
          return {
            ok: false as const,
            error: result.message ?? "Failed to create version",
            field: result.field ?? null,
          };
        }
        return { ok: false as const, error: "Failed to create version", field: null };
      } catch (err) {
        toastMutationError(err, "Failed to create version");
        return { ok: false as const, error: "Failed to create version", field: null };
      }
    },
    [createVersionMutation],
  );

  // Rename is metadata (allowed on published versions too); the cache updates
  // the AutomationVersion entity by id, so no automation payload is needed.
  const renameVersion = useCallback(
    async (versionId: string, name: string) => {
      try {
        const { data } = await renameVersionMutation({
          variables: { versionId, name },
        });
        const result = data?.renameAutomationVersion;
        if (result?.__typename === "AutomationVersion") return true;
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to rename version", type: "error" });
        }
        return false;
      } catch (err) {
        toastMutationError(err, "Failed to rename version");
        return false;
      }
    },
    [renameVersionMutation],
  );

  // Delete a version (the server rejects the live one and the last remaining
  // one). Evicts the version's cache object so it disappears from the pill.
  const deleteVersion = useCallback(
    async (automationId: string, versionId: string) => {
      try {
        const { data } = await deleteVersionMutation({
          variables: { automationId, versionId },
          update: (cache, { data: d }) => {
            if (d?.deleteAutomationVersion?.__typename !== "Automation") return;
            const cacheId = cache.identify({
              __typename: "AutomationVersion",
              id: versionId,
            });
            // An absent `id` reads as the ROOT, so the eviction would drop a
            // ROOT_QUERY field instead of this version.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        const result = data?.deleteAutomationVersion;
        if (result?.__typename === "Automation") {
          return { ok: true as const, automation: result };
        }
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to delete version", type: "error" });
        }
        return { ok: false as const };
      } catch (err) {
        toastMutationError(err, "Failed to delete version");
        return { ok: false as const };
      }
    },
    [deleteVersionMutation],
  );

  // Hard delete (not archive) — permanently removes the automation. Drops the
  // row from every cached GetAutomations connection AND decrements its `total`
  // in the same cache write (then evicts the entity), so the list and its
  // status counts update synchronously — an evict-plus-refetch left the counts
  // stale until the network round-trip landed.
  const deleteAutomation = useCallback(
    async (id: string) => {
      try {
        const { data } = await deleteMutation({
          variables: { id },
          update: (cache) => {
            removeFromConnection(
              cache,
              "automations",
              { __typename: "Automation", id },
              { evict: true },
            );
          },
        });
        if (data?.deleteAutomation) return true;
        toastManager.add({ title: "Failed to delete automation", type: "error" });
        return false;
      } catch (err) {
        toastMutationError(err, "Failed to delete automation");
        return false;
      }
    },
    [deleteMutation],
  );

  return {
    createAutomation,
    updateAutomation,
    saveGraph,
    goLiveVersion,
    createVersion,
    renameVersion,
    deleteVersion,
    deleteAutomation,
  };
}
